import React, { useEffect, useState } from 'react';

import { Alert } from '@mui/material';

import ContentCard from '../../../../components/ContentCard';
import SkeletonStack from '../../../../components/SkeletonStack';
import { getCurrentUserId } from '../../../../services/auth';
import { useGetExamMarkingRequestsQuery } from '../../../../services/exams';
import { ExamMarkingRequest } from '../../../../services/exams/types';
import { ExamRequestStatus } from '../../../../types/common';
import {
  SUBJECTS,
} from '../../../../utils/common/constants';
import { ContentContainer } from '../../index.styles';
import RequestsList from '../RequestsList';

const appendDisplayedFields = (requests: ExamMarkingRequest[]) => requests.map((entry) => ({
  ...entry,
  displayedExamLabel: (entry.examSubject && entry.examLabel) ? `${SUBJECTS[entry.examSubject].label} ${entry.examLabel}` : '???',
}));

const RequestsTab = () => {
  const userId = getCurrentUserId();

  const [assignedRequests, setAssignedRequests] = useState<ExamMarkingRequest[]>([]);
  const [openRequests, setOpenRequests] = useState<ExamMarkingRequest[]>([]);

  const {
    data: assignedRequestsData,
    isLoading: isAssignedRequestsLoading,
  } = useGetExamMarkingRequestsQuery({
    marker: userId,
  });

  const {
    data: openRequestsData,
    isLoading: isOpenRequestsLoading,
  } = useGetExamMarkingRequestsQuery({
    status: ExamRequestStatus.PendingReassignment,
  });

  useEffect(() => {
    if (assignedRequestsData?.data) {
      const filtered = assignedRequestsData.data
        .filter((request) => request.status !== ExamRequestStatus.PendingApproval);

      const mapped = appendDisplayedFields(filtered);
      setAssignedRequests(mapped);
    }
  }, [assignedRequestsData]);

  useEffect(() => {
    if (openRequestsData?.data) {
      const filtered = openRequestsData.data
        // Open requests created by the current user are already displayed in the assigned requests
        .filter((request) => request.marker !== userId);

      const mapped = appendDisplayedFields(filtered);
      setOpenRequests(mapped);
    }
  }, [openRequestsData]);

  if (isAssignedRequestsLoading || isOpenRequestsLoading) {
    return <SkeletonStack />;
  }

  return (
    <ContentContainer>
      <ContentCard variant="outlined" title={`My Requests${assignedRequests ? ` (${assignedRequests.length})` : ''}`}>
        <Alert severity="warning">
          Dropped requests become an open request for other markers,
          but marking the paper by the due date is <b>still your responsibility</b> until
          it is picked up by someone else!
        </Alert>
        <RequestsList
          rows={assignedRequests || []}
          allowDrop
        />
      </ContentCard>
      <ContentCard variant="outlined" title={`Open Requests${openRequests ? ` (${openRequests.length})` : ''}`}>
        <RequestsList
          rows={openRequests || []}
          displayMarker
        />
      </ContentCard>
    </ContentContainer>
  );
};

export default RequestsTab;
